import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Editor from '@monaco-editor/react';
import { knowledgeApi, type KnowledgeArticle } from '@/lib/api';
import { ArrowLeft, Save, Globe, CheckCircle, Clock, Layers, ChevronDown, ChevronUp } from 'lucide-react';
import styles from './_auth.knowledge.$sourceId.articles.$articleId.module.css';

export const Route = createFileRoute('/_auth/knowledge/$sourceId/articles/$articleId')({
  component: ArticleEditorPage,
});

const COUNTRY_NAMES: Record<string, string> = {
  FI: '🇫🇮 Finland', SE: '🇸🇪 Sweden', NO: '🇳🇴 Norway', DK: '🇩🇰 Denmark',
  GB: '🇬🇧 United Kingdom', US: '🇺🇸 United States', DE: '🇩🇪 Germany',
  FR: '🇫🇷 France', NL: '🇳🇱 Netherlands', ES: '🇪🇸 Spain', IT: '🇮🇹 Italy',
  PL: '🇵🇱 Poland', AU: '🇦🇺 Australia', CA: '🇨🇦 Canada',
};
const LANGUAGE_NAMES: Record<string, string> = {
  fi: 'Finnish', sv: 'Swedish', no: 'Norwegian', da: 'Danish',
  en: 'English', de: 'German', fr: 'French', nl: 'Dutch',
  es: 'Spanish', it: 'Italian', pl: 'Polish',
};
const COUNTRIES = Object.keys(COUNTRY_NAMES);
const LANGUAGES = Object.keys(LANGUAGE_NAMES);

export default function ArticleEditorPage() {
  const { sourceId, articleId } = Route.useParams();
  const qc = useQueryClient();

  const { data: article, isLoading } = useQuery({
    queryKey: ['knowledge-article', articleId],
    queryFn: () => knowledgeApi.getArticle(articleId),
  });

  const { data: chunks = [], isLoading: chunksLoading } = useQuery({
    queryKey: ['knowledge-chunks', articleId],
    queryFn: () => knowledgeApi.getChunks(articleId),
    enabled: !!(article as KnowledgeArticle | undefined)?.published,
  });

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [country, setCountry] = useState<string>('');
  const [language, setLanguage] = useState('en');
  const [dirty, setDirty] = useState(false);
  const [showChunks, setShowChunks] = useState(false);
  const [showMeta, setShowMeta] = useState(true);

  useEffect(() => {
    if (article) {
      setTitle((article as KnowledgeArticle).title);
      setContent((article as KnowledgeArticle).content);
      setCountry((article as KnowledgeArticle).country ?? '');
      setLanguage((article as KnowledgeArticle).language);
      setDirty(false);
    }
  }, [article]);

  const saveMutation = useMutation({
    mutationFn: () => knowledgeApi.updateArticle(articleId, {
      title,
      content,
      country: country || null,
      language,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge-article', articleId] });
      qc.invalidateQueries({ queryKey: ['knowledge-articles', sourceId] });
      qc.invalidateQueries({ queryKey: ['knowledge-locales', sourceId] });
      setDirty(false);
    },
  });

  const publishMutation = useMutation({
    mutationFn: () => knowledgeApi.publish(articleId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge-article', articleId] });
      qc.invalidateQueries({ queryKey: ['knowledge-articles', sourceId] });
      qc.invalidateQueries({ queryKey: ['knowledge-chunks', articleId] });
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: () => knowledgeApi.unpublish(articleId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge-article', articleId] });
      qc.invalidateQueries({ queryKey: ['knowledge-articles', sourceId] });
    },
  });

  const art = article as KnowledgeArticle | undefined;

  if (isLoading) return <div className={styles.loading}>Loading article…</div>;
  if (!art) return <div className={styles.loading}>Article not found.</div>;

  const localeName = `${COUNTRY_NAMES[country] ?? (country || '🌐 Global')} · ${LANGUAGE_NAMES[language] ?? language}`;

  return (
    <div className={styles.layout}>
      {/* ── Header ── */}
      <div className={styles.header}>
        <Link to="/knowledge/$sourceId" params={{ sourceId }} className={styles.back}>
          <ArrowLeft size={16} /> Back to source
        </Link>

        <div className={styles.headerCenter}>
          <input
            className={styles.titleInput}
            value={title}
            onChange={e => { setTitle(e.target.value); setDirty(true); }}
            placeholder="Article title…"
          />
          <div className={styles.localePill}>
            <Globe size={13} />
            {localeName}
          </div>
        </div>

        <div className={styles.headerActions}>
          <div className={`${styles.statusBadge} ${art.published ? styles.published : styles.draft}`}>
            {art.published ? <><CheckCircle size={13} /> Published</> : <><Clock size={13} /> Draft</>}
          </div>
          {dirty && (
            <button className={styles.saveBtn} onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              <Save size={14} /> {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          )}
          {art.published ? (
            <button className={styles.unpublishBtn} onClick={() => unpublishMutation.mutate()}>Unpublish</button>
          ) : (
            <button className={styles.publishBtn} onClick={async () => { if (dirty) await saveMutation.mutateAsync(); publishMutation.mutate(); }}>
              Publish
            </button>
          )}
        </div>
      </div>

      <div className={styles.body}>
        {/* ── Left: Editor ── */}
        <div className={styles.editorCol}>
          <div className={styles.editorWrap}>
            <Editor
              height="100%"
              defaultLanguage="markdown"
              value={content}
              onChange={v => { setContent(v ?? ''); setDirty(true); }}
              theme="vs-dark"
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                lineNumbers: 'on',
                wordWrap: 'on',
                scrollBeyondLastLine: false,
                padding: { top: 16, bottom: 16 },
                fontFamily: 'ui-monospace, "Fira Code", monospace',
              }}
            />
          </div>
        </div>

        {/* ── Right: Metadata + Chunks ── */}
        <div className={styles.sideCol}>
          {/* Locale metadata */}
          <div className={styles.sideCard}>
            <button className={styles.sideCardHeader} onClick={() => setShowMeta(s => !s)}>
              <span><Globe size={14} /> Locale</span>
              {showMeta ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showMeta && (
              <div className={styles.metaBody}>
                <label>
                  <span>Country</span>
                  <select value={country} onChange={e => { setCountry(e.target.value); setDirty(true); }}>
                    <option value="">🌐 Global (all countries)</option>
                    {COUNTRIES.map(c => <option key={c} value={c}>{COUNTRY_NAMES[c]}</option>)}
                  </select>
                </label>
                <label>
                  <span>Language</span>
                  <select value={language} onChange={e => { setLanguage(e.target.value); setDirty(true); }}>
                    {LANGUAGES.map(l => <option key={l} value={l}>{LANGUAGE_NAMES[l]} ({l})</option>)}
                  </select>
                </label>
                <div className={styles.localeInfo}>
                  {country
                    ? <>This article is scoped to <strong>{COUNTRY_NAMES[country]}</strong> in <strong>{LANGUAGE_NAMES[language]}</strong>. It will NOT be shown for other countries, even with the same language.</>
                    : <>This is a <strong>global article</strong> in <strong>{LANGUAGE_NAMES[language]}</strong>. It serves as a fallback for all countries where no country-specific version exists.</>
                  }
                </div>
              </div>
            )}
          </div>

          {/* Article info */}
          <div className={styles.sideCard}>
            <div className={styles.infoRow}><span>Last updated</span><span>{new Date(art.updatedAt).toLocaleString()}</span></div>
            {art.indexedAt && <div className={styles.infoRow}><span>Last indexed</span><span>{new Date(art.indexedAt).toLocaleString()}</span></div>}
            <div className={styles.infoRow}><span>Chunks</span><span>{art._count?.chunks ?? 0}</span></div>
          </div>

          {/* Chunks preview */}
          {art.published && (
            <div className={styles.sideCard}>
              <button className={styles.sideCardHeader} onClick={() => setShowChunks(s => !s)}>
                <span><Layers size={14} /> Chunks ({art._count?.chunks ?? 0})</span>
                {showChunks ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {showChunks && (
                <div className={styles.chunksBody}>
                  {chunksLoading ? (
                    <div className={styles.chunkLoading}>Loading chunks…</div>
                  ) : (chunks as Array<{ id: string; chunkIndex: number; content: string; tokenCount: number }>).map(chunk => (
                    <div key={chunk.id} className={styles.chunk}>
                      <div className={styles.chunkHeader}>
                        <span className={styles.chunkNum}>Chunk {chunk.chunkIndex + 1}</span>
                        <span className={styles.chunkTokens}>{chunk.tokenCount} tokens</span>
                      </div>
                      <p className={styles.chunkContent}>{chunk.content}</p>
                    </div>
                  ))}
                  {(chunks as unknown[]).length === 0 && <div className={styles.chunkLoading}>No chunks yet — publish to index.</div>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
